import { useState } from 'react';
import type {
  PortalCoverage,
  PortalGrouping,
  PortalMetric,
  PortalReportLabels,
  PortalSeriesPoint,
} from '../../../../types/portalReport';
import { PORTAL_METRICS } from '../../../../types/portalReport';
import { metricLabel } from '../../../../lib/portalReport/config';
import { coverageNote } from '../../../../lib/portalReport/series';
import {
  buildTrendSeries,
  multiTrendLegibilityWarning,
} from '../../../../lib/portalReport/multiTrend';
import { formatReportingAmount } from '../../../../lib/reportingMoney';
import { MultiTrendChart } from '../../../reporting/MultiTrendChart';

// CLIENT PORTAL round 2, item 6 — the period chart, now plotting any combination
// of Leads / Cost per lead / Spend, each as a line or a bar.
//
// ⚠️ RENDERED BY THE SHARED MultiTrendChart, not a third chart stack. Both staff
// dashboards already plot several metrics on one canvas through it; the portal
// hands it series through its own adapter, exactly as they do. The chart gained
// per-series type + a secondary axis for this, which the social explorer's own
// control had already established as the pattern.
//
// ⚠️ The 13-month chart stays the single-metric ReportChart: it is where the
// no-data band lives, and one long-run metric at a time is what makes a
// thirteen-bar comparison readable.

/** What a metric's per-bucket tooltip figure says. Native currency, never blended. */
function formatFor(
  metric: PortalMetric,
  currency: string | null,
  labels: PortalReportLabels,
): (point: PortalSeriesPoint) => string {
  return (point) => {
    if (metric === 'leads') {
      return point.leads == null
        ? 'No data'
        : point.leads.toLocaleString('en-US', { maximumFractionDigits: 1 });
    }
    if (metric === 'spend') {
      return point.spend == null
        ? 'No data'
        : formatReportingAmount(point.spend, currency ?? 'AUD');
    }
    // ⚠️ CPL. A measured bucket that spent and got nothing says SO, with the
    // spend — it never shows a figure, because the only figure available is 0
    // and zero reads as "leads were free".
    if (point.costPerLead != null) {
      return formatReportingAmount(point.costPerLead, currency ?? 'AUD');
    }
    if (point.coverage === 'no-data') return 'No data';
    if ((point.spend ?? 0) > 0) {
      return `Spent ${formatReportingAmount(point.spend ?? 0, currency ?? 'AUD')} · 0 ${labels.leads.toLowerCase()}`;
    }
    return '—';
  };
}

export function PeriodTrend({
  series,
  labels,
  currency,
  coverage,
  grouping,
  onGroupingChange,
}: {
  series: PortalSeriesPoint[];
  labels: PortalReportLabels;
  currency: string | null;
  coverage: PortalCoverage;
  grouping: PortalGrouping;
  onGroupingChange: (g: PortalGrouping) => void;
}) {
  // Leads alone by default — one clear line, with the others a tick away.
  const [selected, setSelected] = useState<PortalMetric[]>(['leads']);
  const [types, setTypes] = useState<Record<PortalMetric, 'line' | 'bar'>>({
    leads: 'line',
    costPerLead: 'line',
    spend: 'bar',
  });

  const toggleMetric = (metric: PortalMetric) => {
    setSelected((prev) => {
      const next = prev.includes(metric) ? prev.filter((m) => m !== metric) : [...prev, metric];
      // ⚠️ Never leave the chart with nothing plotted — an empty titled chart
      // reads as broken rather than as a choice. Same rule as the staff
      // multi-trend's minimum of one series.
      return next.length === 0 ? prev : next;
    });
  };

  const chartSeries = selected.flatMap((metric) =>
    buildTrendSeries(series, {
      metric,
      label: metricLabel(metric, labels),
      type: types[metric],
      formatted: formatFor(metric, currency, labels),
    }),
  );

  const warning = multiTrendLegibilityWarning(series, selected, labels);
  const note = coverageNote(series, coverage);

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-medium text-foreground">This period</h3>
        <div className="flex flex-wrap items-center gap-2">
          <GroupingSwitch grouping={grouping} onChange={onGroupingChange} />
        </div>
      </div>

      <div data-testid="metric-toggles" className="mb-3 flex flex-wrap items-center gap-2">
        {PORTAL_METRICS.map((metric) => (
          <MetricToggle
            key={metric}
            label={metricLabel(metric, labels)}
            active={selected.includes(metric)}
            type={types[metric]}
            onToggle={() => toggleMetric(metric)}
            onTypeChange={(t) => setTypes((prev) => ({ ...prev, [metric]: t }))}
          />
        ))}
      </div>

      <MultiTrendChart
        title=""
        loading={false}
        data={{
          buckets: series.map((p) => p.bucket),
          // PortalGrouping and TrendGrouping happen to use the same two words.
          grouping,
          series: chartSeries,
          // Segments already express where a line may join; within one, joining
          // across a known zero-lead day is the point (round-2 item 5).
          connectNulls: true,
        }}
      />

      {warning && (
        // ⚠️ Measured from the data, not a blanket caveat — see
        // multiTrendLegibilityWarning. Saying so beats shipping a flat line the
        // reader would take for a flat metric.
        <p className="mt-2 max-w-prose text-xs leading-relaxed text-amber-700">{warning}</p>
      )}
      {note && <p className="mt-2 max-w-prose text-xs leading-relaxed text-muted-foreground">{note}</p>}
    </div>
  );
}

function MetricToggle({
  label,
  active,
  type,
  onToggle,
  onTypeChange,
}: {
  label: string;
  active: boolean;
  type: 'line' | 'bar';
  onToggle: () => void;
  onTypeChange: (t: 'line' | 'bar') => void;
}) {
  return (
    <span className="inline-flex items-center overflow-hidden rounded-md border border-border">
      <button
        type="button"
        onClick={onToggle}
        aria-pressed={active}
        className={
          active
            ? 'px-2.5 py-1 text-xs font-medium'
            : 'px-2.5 py-1 text-xs font-medium text-muted-foreground hover:bg-muted'
        }
        // ⚠️ Branded for the same reason ChannelBlock's chips are, and it is the
        // SAME control on the SAME page — branding one and not the other would
        // read as a bug rather than as restraint.
        style={
          active
            ? {
                backgroundColor: 'var(--portal-accent)',
                color: 'var(--portal-accent-foreground)',
              }
            : undefined
        }
      >
        {label}
      </button>
      {active && (
        <span className="flex border-l border-border">
          {(['line', 'bar'] as const).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => onTypeChange(t)}
              aria-pressed={type === t}
              aria-label={`${label} as ${t}`}
              className={
                type === t
                  ? 'bg-muted px-2 py-1 text-[11px] font-medium text-foreground'
                  : 'px-2 py-1 text-[11px] text-muted-foreground hover:bg-background'
              }
            >
              {t === 'line' ? 'Line' : 'Bar'}
            </button>
          ))}
        </span>
      )}
    </span>
  );
}

function GroupingSwitch({
  grouping,
  onChange,
}: {
  grouping: PortalGrouping;
  onChange: (g: PortalGrouping) => void;
}) {
  return (
    <div className="inline-flex rounded-md border border-border p-0.5" role="group">
      {(['daily', 'monthly'] as const).map((g) => (
        <button
          key={g}
          type="button"
          onClick={() => onChange(g)}
          aria-pressed={grouping === g}
          className={
            grouping === g
              ? 'rounded px-2.5 py-1 text-xs font-medium'
              : 'rounded px-2.5 py-1 text-xs font-medium text-muted-foreground hover:bg-muted'
          }
          style={
            grouping === g
              ? {
                  backgroundColor: 'var(--portal-accent)',
                  color: 'var(--portal-accent-foreground)',
                }
              : undefined
          }
        >
          {g === 'daily' ? 'Daily' : 'Monthly'}
        </button>
      ))}
    </div>
  );
}
